/**
 * Root Store — 游戏进度、角色、亲友、幕间结果
 *
 * 管理跨幕的全局状态，包括角色数据、亲友团、各幕结果。
 * 各幕的具体游戏状态由对应的 actXStore 管理。
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Character } from '@shared/types/character';
import type { FamilyMember } from '@shared/types/family';
import type { GameStep, Act1Result, Act2Result, Act3Result, FinalReport } from '@shared/types/gameState';

interface RootStoreState {
  step: GameStep;

  // 主角
  protagonistA: Character | null;
  protagonistB: Character | null;

  // 亲友团
  familyA: FamilyMember[];
  familyB: FamilyMember[];

  // 幕间结果
  act1Result: Act1Result | null;
  act2Result: Act2Result | null;
  act3Result: Act3Result | null;

  // 复盘报告
  finalReport: FinalReport | null;
}

interface RootStoreActions {
  // 导航
  goToMenu: () => void;
  goToCharacterCreate: () => void;

  // 角色设置
  setProtagonists: (a: Character, b: Character) => void;
  goToFamilySelect: () => void;

  // 亲友设置
  setFamily: (familyA: FamilyMember[], familyB: FamilyMember[]) => void;
  startAct1: () => void;

  // 幕间流转
  finishAct1: (result: Act1Result) => void;
  startAct2Setup: () => void;
  startAct2: () => void;
  finishAct2: (result: Act2Result) => void;
  startAct3: () => void;
  finishAct3: (result: Act3Result) => void;
  showFinalReport: (report: FinalReport) => void;

  // 直接跳转（用于地下情等特殊流程）
  setStep: (step: GameStep) => void;

  // 重置
  resetAll: () => void;
}

export type RootStore = RootStoreState & RootStoreActions;

const INITIAL_STATE: RootStoreState = {
  step: 'MENU',
  protagonistA: null,
  protagonistB: null,
  familyA: [],
  familyB: [],
  act1Result: null,
  act2Result: null,
  act3Result: null,
  finalReport: null,
};

export const useRootStore = create<RootStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      // ── 导航 ──────────────────────────────────────────────
      goToMenu: () => set({ ...INITIAL_STATE }),

      goToCharacterCreate: () => set({ step: 'CHARACTER_CREATE' }),

      // ── 角色设置 ──────────────────────────────────────────
      setProtagonists: (a, b) => set({ protagonistA: a, protagonistB: b }),

      goToFamilySelect: () => set({ step: 'FAMILY_SELECT' }),

      // ── 亲友设置 ──────────────────────────────────────────
      setFamily: (familyA, familyB) => set({ familyA, familyB }),

      startAct1: () => set({ step: 'ACT1_GAME' }),

      // ── 幕间流转 ──────────────────────────────────────────
      finishAct1: (result) => set({ act1Result: result, step: 'ACT1_RESULT' }),

      startAct2Setup: () => set({ step: 'ACT2_SETUP' }),

      startAct2: () => set({ step: 'ACT2_GAME' }),

      finishAct2: (result) => set({ act2Result: result, step: 'ACT2_RESULT' }),

      startAct3: () => set({ step: 'ACT3_GAME' }),

      finishAct3: (result) => set({ act3Result: result, step: 'ACT3_RESULT' }),

      showFinalReport: (report) => set({ finalReport: report, step: 'FINAL_REPORT' }),

      setStep: (step) => set({ step }),

      // ── 重置 ──────────────────────────────────────────────
      resetAll: () => set({ ...INITIAL_STATE }),
    }),
    {
      name: 'cm-v2-root',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
